import { useFormikContext, Form } from 'formik'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  Switch,
  FormControlLabel,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { ERewardPointsTypes, ETranslationNs, IRewardPointsForm } from 'src/types'
import { HelperWrapper } from 'src/components/helper-wrapper'

import { useStyles } from '../styles'
import { TextInputWithHelper } from 'src/components/text-input-with-helper-icon'
import { Box } from '@mui/system'
import { useEffect, useRef } from 'react'

interface IProps {
  isUpdate?: boolean
}

export const RewardPointForm = ({ isUpdate = false }: IProps) => {
  const [t] = useTranslation([ETranslationNs.Common, ETranslationNs.Form])
  const { errors, values, handleChange, isSubmitting, dirty, setFieldValue, handleBlur, touched } =
    useFormikContext<IRewardPointsForm>()
  const classes = useStyles()

  const defaultRef = useRef(values.default)

  useEffect(() => {
    if (defaultRef.current === undefined) {
      defaultRef.current = values.default
    }
  }, [])

  return (
    <Form className={`${classes.formWrapper} ${classes.formWrapperWithHeight}`}>
      <div className={classes.inputsWrapper}>
        <Typography className={classes.title}>{isUpdate ? 'Update' : 'Create'}</Typography>
        <div className={classes.inputsWrapper}>
          <TextInputWithHelper
            inputName="name"
            handleChange={handleChange}
            handleBlur={handleBlur}
            value={values.name}
            inputUpperLabel={
              <HelperWrapper
                t={'reward-points-name-input-label'}
                ns={ETranslationNs.Common}
                isInputLable
                isMandatory
              />
            }
            inputPlaceholder={t('reward-points-name-input-placeholder.v', {
              ns: ETranslationNs.Common,
            })}
            error={touched.name && errors.name ? <HelperWrapper t={errors.name} /> : null}
            fullWidth
            withoutPlaceholderPrepend
          />
          <TextInputWithHelper
            inputName="description"
            handleChange={handleChange}
            handleBlur={handleBlur}
            value={values.description}
            inputUpperLabel={
              <HelperWrapper
                t={'reward-points-description-input-label'}
                ns={ETranslationNs.Common}
                isInputLable
                isMandatory
              />
            }
            inputPlaceholder={t('reward-points-description-input-placeholder.v', {
              ns: ETranslationNs.Common,
            })}
            error={
              touched.description && errors.description ? (
                <HelperWrapper t={errors.description} />
              ) : null
            }
            fullWidth
            withoutPlaceholderPrepend
          />
          <FormControl variant="standard" fullWidth>
            <InputLabel>{t('reward-points-type-input-label.v')}</InputLabel>
            <Select
              name="type"
              value={values.type}
              onChange={(e) => setFieldValue('type', e.target.value)}
              size="small"
              autoWidth
              MenuProps={{ style: { maxHeight: '400px' } }}
              sx={{
                fontSize: '12px',
                border: 'none',
                boxShadow: 'none',
                '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
              }}
            >
              {Object.entries(ERewardPointsTypes).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            autoComplete="off"
            variant="standard"
            id="amount"
            name="amount"
            onChange={handleChange}
            type="number"
            label={t('reward-points-amount-input-label.v')}
            value={values.amount}
            helperText={
              <Typography className={classes.warningText}>
                {errors.amount && <HelperWrapper t={errors.amount} />}&nbsp;
              </Typography>
            }
          />
          {values.type === ERewardPointsTypes.REFERRAL_PROGRAM && (
            <TextField
              autoComplete="off"
              variant="standard"
              id="scans_to_complete"
              name="scans_to_complete"
              onChange={handleChange}
              type="number"
              label={t('reward-points-scan-to-complete-input-label.v')}
              value={values.scans_to_complete}
              helperText={
                <Typography className={classes.warningText}>
                  {errors.scans_to_complete && <HelperWrapper t={errors.scans_to_complete} />}&nbsp;
                </Typography>
              }
            />
          )}
          {isUpdate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Typography>
                <HelperWrapper t={'reward-points-default-input-label'} />
              </Typography>
              <Switch
                size="small"
                checked={values.default}
                disabled={!isUpdate || defaultRef.current}
                onChange={(e) => setFieldValue('default', e.target.checked)}
              />
            </Box>
          )}
        </div>
      </div>
      <Button
        variant="contained"
        disabled={isSubmitting || !dirty || Object.keys(errors).length > 0}
        type="submit"
      >
        <HelperWrapper t="save-button-text" />
      </Button>
    </Form>
  )
}
